import { useEffect, useState } from "react";
import { ArrowRight, CalendarCheck, Loader2, ShieldCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { z } from "zod";
import { useServerFn } from "@tanstack/react-start";
import { submitAuditRequest } from "@/lib/audit-request.functions";
import { campaignTags } from "@/lib/campaign";
import { WHATSAPP_URL } from "@/lib/company";
import { FIT_REVIEW_NOTE } from "@/lib/pricing";
import { trackPixel, trackPixelCustom } from "@/lib/meta-pixel";

const schema = z.object({
  name: z.string().trim().min(2, "Please enter your name").max(100),
  email: z.string().trim().email("Enter a valid email").max(255),
  whatsapp: z
    .string()
    .trim()
    .max(30, "Please keep this under 30 characters")
    .regex(/^[+\d\s().-]*$/, "Use digits, spaces and a leading +")
    .refine(
      (v) => v === "" || v.replace(/\D/g, "").length >= 7,
      "Enter the full number, with the country code",
    ),
  website: z.string().trim().min(3, "Enter your website").max(255),
  spend: z.string().min(1, "Select your budget"),
  notes: z.string().trim().max(1000, "Please keep this under 1,000 characters"),
});

const EMPTY_FORM = { name: "", email: "", whatsapp: "", website: "", spend: "", notes: "" };

// Ranges for agencies and developers selling high-value property. "Not
// advertising yet" stays, because a developer before launch is a good fit.
const SPEND_OPTIONS = [
  "Not advertising yet",
  "Under $3,000 / mo",
  "$3,000 – $10,000 / mo",
  "$10,000 – $30,000 / mo",
  "$30,000 – $100,000 / mo",
  "$100,000+ / mo",
];

/**
 * Exactly what the connection can and cannot do.
 *
 * Someone is being asked for access to the account that spends their money,
 * and that ask is only reasonable if its limits are stated before the click
 * rather than buried in a policy. Each line names something checkable — the
 * literal scope, the specific fields, the exact place to revoke — because a
 * buyer sophisticated enough to hesitate is sophisticated enough to verify.
 */
const GUARANTEES = [
  {
    head: "Read-only, and named",
    body: "The permission requested is ads_read. It cannot create, edit, pause or delete anything in the account, and it cannot spend.",
  },
  {
    head: "Performance data, nothing else",
    body: "Spend, impressions, clicks, cost metrics and campaign structure. Not messages, not customer records, not audience membership.",
  },
  {
    head: "Yours to revoke",
    body: "Removable at any moment in Meta's Business Integrations settings, without involving Adkiro. The stored token is encrypted and expires in about 60 days.",
  },
];

type Path = "connect" | "form";

/**
 * Whether a stranger can complete the Meta connection yet.
 *
 * The Adkiro app stays unpublished until App Review passes, and until then
 * Meta refuses the consent dialog to anyone without a role on the app. The
 * connect path was the dialog's default tab, which made it a dead end for
 * every real visitor: the obvious button led to a Meta error page. With this
 * false, the dialog opens on the form alone and the connect path is reachable
 * only through /#connect — for the App Review screencast, and for testing.
 * Set it to true on the day the app is switched to Live.
 */
export const META_CONNECT_PUBLIC = false;

export function BookingModal({
  open,
  onOpenChange,
  initialPath = "form",
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  initialPath?: Path;
}) {
  const [path, setPath] = useState<Path>(initialPath);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  const submitRequest = useServerFn(submitAuditRequest);

  // Campaigns optimise on this rather than on the submit below: at mandate
  // volumes the submit alone never reaches Meta's learning-phase threshold.
  useEffect(() => {
    if (open) trackPixelCustom("ConsultationStarted");
  }, [open]);

  // The dialog stays mounted between openings, so the path it opens on has to
  // be applied each time rather than only on first render.
  useEffect(() => {
    if (open) setPath(initialPath);
  }, [open, initialPath]);

  const showConnect = META_CONNECT_PUBLIC || initialPath === "connect";

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) next[String(issue.path[0])] = issue.message;
      setErrors(next);
      return;
    }
    setErrors({});
    setSending(true);
    try {
      const eventId = crypto.randomUUID();
      const result = await submitRequest({
        data: { ...parsed.data, eventId, campaign: campaignTags() },
      });
      trackPixel("Lead", { content_name: parsed.data.spend }, eventId);
      onOpenChange(false);
      setForm(EMPTY_FORM);
      // Only promise the confirmation email when the server says it went.
      toast.success("Free review requested", {
        description: result.confirmed
          ? `Kristian replies personally within one business day. A confirmation is on its way to ${parsed.data.email}; if it isn't in your inbox, check spam.`
          : "Kristian replies personally within one business day.",
      });
    } catch {
      toast.error("Something went wrong", {
        description: "Your request wasn't sent. Please try again, or email us directly.",
      });
    } finally {
      setSending(false);
    }
  };

  const tab = (value: Path, label: string, note: string) => {
    const active = path === value;
    return (
      <button
        type="button"
        onClick={() => setPath(value)}
        aria-pressed={active}
        className={`flex-1 border px-4 py-3 text-left transition-colors ${
          active
            ? "border-primary bg-primary/[0.06]"
            : "border-border text-muted-foreground hover:border-primary/40"
        }`}
      >
        <span className={`block text-sm font-medium ${active ? "text-foreground" : ""}`}>
          {label}
        </span>
        <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">{note}</span>
      </button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <CalendarCheck className="size-5 text-primary" />
            Get a free review
          </DialogTitle>
          <DialogDescription>{FIT_REVIEW_NOTE}</DialogDescription>
        </DialogHeader>

        {showConnect && (
          <div className="flex gap-2">
            {tab("connect", "Connect the account", "Read-only. About 30 seconds.")}
            {tab("form", "Send details instead", "About a minute, no access.")}
          </div>
        )}

        {showConnect && path === "connect" ? (
          <div className="space-y-4">
            <p className="text-sm leading-relaxed text-muted-foreground">
              Adkiro reads the account directly and comes back with a diagnosis, rather than asking
              you to assemble numbers first. Nothing is changed and nothing is spent.
            </p>

            <ul className="space-y-3 border-l-2 border-primary pl-4">
              {GUARANTEES.map((g) => (
                <li key={g.head}>
                  <p className="flex items-center gap-1.5 text-xs font-medium">
                    <ShieldCheck className="size-3.5 shrink-0 text-primary" />
                    {g.head}
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{g.body}</p>
                </li>
              ))}
            </ul>

            {/*
              A plain anchor, not a router Link: this is an OAuth redirect and
              the browser has to leave the app for Meta's consent screen.
            */}
            <Button variant="brand" size="lg" className="w-full" asChild>
              <a href="/api/meta/connect" onClick={() => trackPixelCustom("MetaConnectStarted")}>
                Connect ad account <ArrowRight className="size-4" />
              </a>
            </Button>

            <p className="text-center text-xs text-muted-foreground">
              Meta shows you exactly what is being granted before you approve. Full detail in the{" "}
              <a href="/privacy" className="text-primary hover:text-foreground">
                privacy notice
              </a>
              .
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <p className="border-l-2 border-primary pl-4 text-xs leading-relaxed text-muted-foreground">
              Nothing is paid until a service and its price are agreed in writing. The prices are on
              the{" "}
              <a href="/meta-ads-pricing" className="text-primary hover:text-foreground">
                services page
              </a>
              .
            </p>
            <div className="space-y-2">
              <Label htmlFor="b-name">Full name</Label>
              <Input
                id="b-name"
                value={form.name}
                maxLength={100}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jordan Miles"
              />
              {errors["name"] && <p className="text-xs text-destructive">{errors["name"]}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="b-email">Email</Label>
              <Input
                id="b-email"
                type="email"
                value={form.email}
                maxLength={255}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="jordan@agency.com"
              />
              {errors["email"] && <p className="text-xs text-destructive">{errors["email"]}</p>}
            </div>
            {/* A second way to reach the prospect when the reply lands in spam. */}
            <div className="space-y-2">
              <Label htmlFor="b-whatsapp">
                WhatsApp <span className="font-normal text-muted-foreground">(optional)</span>
              </Label>
              <Input
                id="b-whatsapp"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={form.whatsapp}
                maxLength={30}
                onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                placeholder="+971 50 123 4567"
              />
              <p className="text-xs text-muted-foreground">
                In case the email reply lands in spam. Messages only, no calls.
              </p>
              {errors["whatsapp"] && (
                <p className="text-xs text-destructive">{errors["whatsapp"]}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="b-site">Website or listing link</Label>
              <Input
                id="b-site"
                value={form.website}
                maxLength={255}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                placeholder="youragency.com"
              />
              {errors["website"] && <p className="text-xs text-destructive">{errors["website"]}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="b-spend">Monthly advertising budget</Label>
              <Select value={form.spend} onValueChange={(v) => setForm({ ...form, spend: v })}>
                <SelectTrigger id="b-spend" aria-label="Monthly advertising budget">
                  <SelectValue placeholder="Select a range" />
                </SelectTrigger>
                <SelectContent>
                  {SPEND_OPTIONS.map((o) => (
                    <SelectItem key={o} value={o}>
                      {o}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors["spend"] && <p className="text-xs text-destructive">{errors["spend"]}</p>}
            </div>
            {/*
              Optional, and the most useful thing the form asks. Website and
              budget say which service could fit; this says what is to be
              sold, which is what the reply has to address.
            */}
            <div className="space-y-2">
              <Label htmlFor="b-notes">
                What would you like to advertise?{" "}
                <span className="font-normal text-muted-foreground">(optional)</span>
              </Label>
              <Textarea
                id="b-notes"
                value={form.notes}
                maxLength={1000}
                rows={3}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="For example: three apartments in Dubai Marina, or a new project launching in March."
              />
              {errors["notes"] && <p className="text-xs text-destructive">{errors["notes"]}</p>}
            </div>
            <Button type="submit" variant="brand" size="lg" className="w-full" disabled={sending}>
              {sending && <Loader2 className="size-4 animate-spin" />}
              {sending ? "Sending…" : "Get a free review"}
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              Questions first?{" "}
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:text-foreground"
              >
                Message on WhatsApp
              </a>
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
